import backendCommunicator from 'services/backendCommunicator';

import * as I from 'interfaces';

import {gramsToKilos, localDateSender} from 'helpers/UnitsFormatHandler';

import {type AxiosError} from 'axios';
import Alert from 'helpers/Alert';
import {type TFunction} from 'i18next';
import {mutate} from 'swr';
import {type Dispatch, type SetStateAction} from 'react';
import {
  type InputPost,
  type InputPut,
  HarvestDemandPutSpeciesEnum,
  type SimpleInputResponse,
} from 'client/bioplanModels';
import {mutateAll} from 'hooks/swr/swrHookFactory';

const inputsToSend = (input: I.InputsState, t: TFunction): InputPost[] | InputPost | InputPut | undefined => {
  if (input.treeSelection.cageIds.length === 0) {
    Alert.warning(t('noCageError'));
  } else if (!input.date) {
    Alert.warning(t('noDate'));
  } else if (input.count < 1000) {
    Alert.warning(t('inputMinimumCountWarning'));
  } else if (input.id) {
    const date = localDateSender(input.date);
    const inputPut: InputPut = {
      ...input,
      inputId: input.id,
      date,
      count: parseInt(input.count.toString()),
      weight: gramsToKilos(input.weight),
    };
    return inputPut;
  } else if (input.treeSelection.cageIds.length === 1) {
    const date = localDateSender(input.date);
    const inputsPost: InputPost = {
      ...input,
      date,
      count: parseInt(input.count.toString()),
      weight: gramsToKilos(input.weight),
      cageId: input.treeSelection.cageIds[0],
      isLocked: input.isLocked ?? true,
    };
    return inputsPost;
  } else if (input.treeSelection.cageIds.length > 1) {
    const date = localDateSender(input.date);
    const inputsPost: InputPost[] = input.treeSelection.cageIds.map((cageId) => ({
      ...input,
      date,
      count: parseInt(input.count.toString()),
      weight: gramsToKilos(input.weight),
      cageId,
      isLocked: input.isLocked ?? true,
    }));
    return inputsPost;
  } else {
    Alert.error('No cages selected for input');
  }
};

export const initialState = (planId: string, startDate?: Date, formData?: I.IFormData): I.InputsState => {
  return {
    planId,
    date: formData?.date ?? startDate ?? new Date(),
    count: formData?.count ?? 0,
    weight: formData?.weight ?? 0,
    treeSelection: {
      siteIds: formData?.siteId ? [formData.siteId] : [],
      cageIds: formData?.cageId ? [formData.cageId] : [],
    },
    id: formData?.id,
    fishTypeId: formData?.fishTypeId ?? '',
    companyId: '',
    smoltSupplyId: null,
    certificates: [],
    species: HarvestDemandPutSpeciesEnum.Salmon,
    fishGroupName: null,
    fishGroupPropertyIds: [],
    isLocked: formData?.isLocked ?? true,
  };
};

export const handleCommunicationSuccess = ({setInputs: setCageInput, planId}: IHandleCommunicationSuccess) => {
  const emptyInput = initialState(planId, undefined, undefined);
  setCageInput(emptyInput);
  void mutate([planId, I.SWRKeyEnum.GET_CAGES_WITH_UNALLOCATED_BIOMASS]);
  void mutateAll(I.SWRKeyEnum.GET_UNASSIGNED_INPUTS_TO_SMOLT_SUPPLY);
};

function isInputsPut(inputs: InputPut | InputPost | InputPost[]): inputs is InputPut {
  return !Array.isArray(inputs) && 'inputId' in inputs;
}

function isInputsPost(inputs: InputPut | InputPost | InputPost[]): inputs is InputPost {
  return !Array.isArray(inputs) && !('inputId' in inputs);
}

function isInputsBatchPost(inputs: InputPut | InputPost | InputPost[]): inputs is InputPost[] {
  return Array.isArray(inputs);
}

const backendOperator = async (method: I.methodType, inputs: InputPut | InputPost | InputPost[]) => {
  if (method === I.methodType.edit && isInputsPut(inputs)) {
    return await backendCommunicator.updateCageInput([inputs]);
  } else if (method === I.methodType.add && isInputsBatchPost(inputs)) {
    return await backendCommunicator.addCageBatchInput(inputs);
  } else if (method === I.methodType.add && isInputsPost(inputs)) {
    return await backendCommunicator.addCageInput(inputs);
  } else {
    Alert.error('Invalid input data format');
  }
};

const addEditInputBackendHandler = async ({
  method,
  inputs,
  createCageInput,
  communicationSuccess,
  t,
}: IHandleCommunication) => {
  const _input = createCageInput && inputsToSend(inputs, t);
  const backendOperation = _input && backendOperator(method, _input);

  const response =
    backendOperation &&
    backendOperation
      .then((response) => {
        if (response) {
          communicationSuccess(Array.isArray(response.data) ? response.data : [response.data]);
        }
        return response;
      })
      .catch((error: AxiosError) => {
        Alert.error(error);
      });
  return await response;
};

export default addEditInputBackendHandler;

interface IHandleCommunication {
  method: I.methodType;
  inputs: I.InputsState;
  createCageInput: boolean;
  inputId?: string;
  communicationSuccess: (input: SimpleInputResponse[]) => void;
  t: TFunction;
}

interface IHandleCommunicationSuccess {
  setInputs: Dispatch<SetStateAction<I.InputsState>>;
  planId: string;
}
